import { Link, NavLink, useNavigate } from "react-router-dom";
import { useCallback, useEffect, useRef, useState } from "react";
import { auth } from "../../firebase";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { useNavCounts } from "../../utils/navCounts";
import { useNewVersion, useServiceWorkerBridge } from "../../utils/live";
import SearchOverlay, { SearchIcon } from "./SearchOverlay";

// The four things the dashboard is for: new leads, what to chase, the jobs
// themselves, and how it's all going. Calendar and Customers sit under More —
// Google Calendar and HubSpot already cover those day to day.
const NAV = [
  { to: "/admin", label: "Inbox", end: true, icon: InboxIcon, badge: "inbox" },
  { to: "/admin/followups", label: "Follow-ups", icon: BellIcon, badge: "followups" },
  { to: "/admin/jobs", label: "Jobs", icon: JobsIcon },
  { to: "/admin/numbers", label: "Numbers", icon: ChartIcon },
];

const MORE = [
  { to: "/admin/calendar", label: "Calendar" },
  { to: "/admin/customers", label: "Customers" },
  { to: "/admin/services", label: "Services" },
  { to: "/admin/expenses", label: "Expenses" },
  { to: "/admin/plan", label: "Full-time plan" },
  { to: "/admin/reports", label: "Reports" },
  { to: "/admin/quote-page", label: "Quote page" },
  { to: "/admin/email", label: "Email" },
  { to: "/admin/alerts", label: "Alerts" },
  { to: "/admin/settings", label: "Settings" },
];

export default function AppShell({ children, title, action, wide = false }) {
  const maxW = wide ? "max-w-6xl" : "max-w-4xl";
  const [user, setUser] = useState(null);
  const [moreOpen, setMoreOpen] = useState(false); // desktop dropdown
  const [sheetOpen, setSheetOpen] = useState(false); // phone bottom sheet
  const [searchOpen, setSearchOpen] = useState(false);
  const closeSearch = useCallback(() => setSearchOpen(false), []);
  const counts = useNavCounts();
  const navigate = useNavigate();
  const moreRef = useRef(null);
  useServiceWorkerBridge();
  const updateReady = useNewVersion({ autoApply: true });

  // "/" or Cmd/Ctrl-K opens search, unless you're typing somewhere.
  useEffect(() => {
    const onKey = (e) => {
      const typing = /input|textarea|select/i.test(e.target?.tagName || "") || e.target?.isContentEditable;
      if ((e.key === "k" && (e.metaKey || e.ctrlKey)) || (e.key === "/" && !typing)) {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => onAuthStateChanged(auth, (u) => setUser(u || null)), []);

  // Close the More menu on an outside tap.
  useEffect(() => {
    if (!moreOpen) return undefined;
    const close = (e) => {
      if (moreRef.current && !moreRef.current.contains(e.target)) setMoreOpen(false);
    };
    document.addEventListener("pointerdown", close);
    return () => document.removeEventListener("pointerdown", close);
  }, [moreOpen]);

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      navigate("/admin/login", { replace: true });
    } catch (e) {
      console.error("Sign out error:", e);
    }
  };

  const moreLinks = (onPick) => (
    <>
      {MORE.map((m) => (
        <Link
          key={m.to}
          to={m.to}
          onClick={onPick}
          className="block px-4 py-2.5 text-sm font-medium text-birdie-charcoal hover:bg-birdie-cream"
        >
          {m.label}
        </Link>
      ))}
      <div className="border-t border-birdie-green/10 my-1" />
      <button
        onClick={handleSignOut}
        className="w-full text-left px-4 py-2.5 text-sm font-medium text-red-600 hover:bg-red-50"
        title={user?.email || ""}
      >
        Sign out
      </button>
    </>
  );

  return (
    <div className="min-h-screen bg-birdie-beige">
      <header className="sticky top-0 z-30 bg-birdie-cream/95 backdrop-blur border-b border-birdie-green/10">
        <div className={`mx-auto ${maxW} px-4 h-14 flex items-center justify-between gap-3`}>
          <Link to="/admin" className="flex items-center gap-2 min-w-0">
            <img src="/birdie-bus-logo.png" alt="Birdie Bus" className="h-8 w-auto" />
          </Link>

          <div className="sm:hidden flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => setSearchOpen(true)}
              className="flex items-center gap-2 rounded-full border border-birdie-green/20 bg-white px-3 py-1.5 text-sm text-birdie-charcoal/55"
              aria-label="Search customers and jobs"
            >
              <SearchIcon className="w-4 h-4" />
              Search
            </button>
            <SettingsLink />
          </div>

          {/* Desktop nav */}
          <nav className="hidden sm:flex items-center gap-1">
            <button
              type="button"
              onClick={() => setSearchOpen(true)}
              className="flex items-center gap-2 mr-1 rounded-lg border border-birdie-green/20 bg-white px-3 py-1.5 text-sm text-birdie-charcoal/50 hover:text-birdie-charcoal"
              aria-label="Search customers and jobs"
              title="Search (/ or Ctrl-K)"
            >
              <SearchIcon className="w-4 h-4" />
              Search
            </button>
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                className={({ isActive }) =>
                  `relative px-3 py-1.5 rounded-lg text-sm font-semibold transition ${
                    isActive ? "bg-birdie-green text-white" : "text-birdie-charcoal/70 hover:bg-birdie-green/10"
                  }`
                }
              >
                {n.label}
                {n.badge && counts[n.badge] > 0 && <Badge n={counts[n.badge]} />}
              </NavLink>
            ))}
            <div className="relative" ref={moreRef}>
              <button
                onClick={() => setMoreOpen((v) => !v)}
                className="px-3 py-1.5 rounded-lg text-sm font-semibold text-birdie-charcoal/70 hover:bg-birdie-green/10"
                aria-expanded={moreOpen}
              >
                More ▾
              </button>
              {moreOpen && (
                <div className="absolute right-0 mt-1 w-44 rounded-xl bg-white border border-birdie-green/15 shadow-birdie-lift py-1">
                  {moreLinks(() => setMoreOpen(false))}
                </div>
              )}
            </div>
            <SettingsLink />
          </nav>
        </div>
      </header>

      {updateReady && (
        <div className="bg-birdie-green text-white text-sm">
          <div className={`mx-auto ${maxW} px-4 py-2 flex items-center justify-between gap-3`}>
            <span>A new version of the dashboard is ready.</span>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="shrink-0 rounded-lg bg-white/15 px-3 py-1 font-semibold hover:bg-white/25"
            >
              Refresh
            </button>
          </div>
        </div>
      )}

      {searchOpen && <SearchOverlay onClose={closeSearch} />}

      {/* pb leaves room for the fixed bottom bar on phones */}
      <main className={`mx-auto ${maxW} px-4 py-4 pb-28 sm:pb-10`}>
        {(title || action) && (
          <div className="flex items-center justify-between gap-3 mb-4">
            {title && (
              <h1 className="font-heading text-2xl sm:text-3xl text-birdie-green tracking-wide">{title}</h1>
            )}
            {action}
          </div>
        )}
        {children}
      </main>

      {/* Mobile bottom nav */}
      <nav className="sm:hidden fixed bottom-0 inset-x-0 z-30 bg-birdie-cream border-t border-birdie-green/15 pb-[env(safe-area-inset-bottom)]">
        <div className="grid grid-cols-5">
          {NAV.map((n) => {
            const Icon = n.icon;
            return (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                className={({ isActive }) =>
                  `flex flex-col items-center justify-center gap-0.5 py-2.5 text-[10px] font-semibold transition ${
                    isActive ? "text-birdie-green" : "text-birdie-charcoal/45"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    <span className="relative">
                      <Icon active={isActive} />
                      {n.badge && counts[n.badge] > 0 && <Badge n={counts[n.badge]} dot />}
                    </span>
                    {n.label}
                  </>
                )}
              </NavLink>
            );
          })}
          <button
            onClick={() => setSheetOpen(true)}
            className="flex flex-col items-center justify-center gap-0.5 py-2.5 text-[10px] font-semibold text-birdie-charcoal/45"
          >
            <MoreIcon />
            More
          </button>
        </div>
      </nav>

      {/* Mobile More sheet */}
      {sheetOpen && (
        <div className="sm:hidden fixed inset-0 z-40 bg-black/30" onClick={() => setSheetOpen(false)}>
          <div
            className="absolute bottom-0 inset-x-0 bg-white rounded-t-2xl pt-2 pb-[calc(env(safe-area-inset-bottom)+12px)]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mx-auto h-1 w-10 rounded-full bg-birdie-charcoal/15 mb-2" />
            {moreLinks(() => setSheetOpen(false))}
          </div>
        </div>
      )}
    </div>
  );
}

function Badge({ n, dot }) {
  const text = n > 99 ? "99+" : String(n);
  return dot ? (
    <span className="absolute -top-1.5 -right-2.5 min-w-[16px] h-4 px-1 rounded-full bg-red-600 text-white text-[9px] leading-4 text-center">
      {text}
    </span>
  ) : (
    <span className="ml-1.5 inline-block min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] leading-[18px] text-center">
      {text}
    </span>
  );
}

const ico = "w-5 h-5";
function InboxIcon({ active }) {
  return (
    <svg className={ico} fill={active ? "currentColor" : "none"} stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 13h5l1.5 3h5L16 13h5M5 5h14l2 8v6a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-6l2-8Z" />
    </svg>
  );
}
function BellIcon({ active }) {
  return (
    <svg className={ico} fill={active ? "currentColor" : "none"} stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 16V11a6 6 0 1 1 12 0v5l2 2H4l2-2Zm4 4h4" />
    </svg>
  );
}
function JobsIcon({ active }) {
  return (
    <svg className={ico} fill={active ? "currentColor" : "none"} stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" d="M4 5h16M4 12h16M4 19h10" />
    </svg>
  );
}
function ChartIcon({ active }) {
  return (
    <svg className={ico} fill="none" stroke="currentColor" strokeWidth={active ? 2.6 : 2} viewBox="0 0 24 24">
      <path strokeLinecap="round" d="M5 20V12M12 20V5M19 20v-9M3 20h18" />
    </svg>
  );
}
function MoreIcon() {
  return (
    <svg className={ico} fill="currentColor" viewBox="0 0 24 24">
      <circle cx="5" cy="12" r="1.8" />
      <circle cx="12" cy="12" r="1.8" />
      <circle cx="19" cy="12" r="1.8" />
    </svg>
  );
}

function SettingsLink() {
  return (
    <NavLink
      to="/admin/settings"
      aria-label="Settings"
      title="Settings"
      className={({ isActive }) =>
        `flex items-center justify-center h-9 w-9 min-h-0 min-w-0 rounded-full ${
          isActive ? "bg-birdie-green text-white" : "text-birdie-charcoal/55 hover:bg-birdie-green/10"
        }`
      }
    >
      <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24" aria-hidden>
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M10.3 4.3c.4-1.7 3-1.7 3.4 0a1.7 1.7 0 0 0 2.6 1.1c1.5-.9 3.3.9 2.4 2.4a1.7 1.7 0 0 0 1 2.6c1.8.4 1.8 3 0 3.4a1.7 1.7 0 0 0-1 2.6c.9 1.5-.9 3.3-2.4 2.4a1.7 1.7 0 0 0-2.6 1c-.4 1.8-3 1.8-3.4 0a1.7 1.7 0 0 0-2.6-1c-1.5.9-3.3-.9-2.4-2.4a1.7 1.7 0 0 0-1-2.6c-1.8-.4-1.8-3 0-3.4a1.7 1.7 0 0 0 1-2.6c-.9-1.5.9-3.3 2.4-2.4a1.7 1.7 0 0 0 2.6-1.1z"
        />
        <circle cx="12" cy="12" r="3" />
      </svg>
    </NavLink>
  );
}
